import { useMemo } from 'react';
import { Feature } from '@weave/schema-gen-ts/dist/shared/feature/location_feature.pb';
import { FeatureTrialDateRange, useFeatureTrialExpiredLocations } from '@frontend/api-platform-location-feature';
import { useCallIntelSubscription } from './use-call-intel-subscription';

type UseCallIntelTrialExpiredLocationsParams = {
  locationIds: string[];
};

type UseCallIntelTrialExpiredLocations = {
  trialActiveLocationIds: string[];
  trialExpiredLocationIds: string[];
  trialExpiredDateRangeMap: Record<string, FeatureTrialDateRange>;
  callIntelProPlanLocationIds: string[];
  hasActiveTrial: boolean;
  hasExpiredTrial: boolean;
  hasActiveSubscription: boolean;
  isLoading: boolean;
};
/**
 * Combines current Call Intelligence subscription state with trial history
 * to determine the active, expired, and subscribed state of the selected locations.
 */
export const useCallIntelTrialExpiredLocations = ({
  locationIds,
}: UseCallIntelTrialExpiredLocationsParams): UseCallIntelTrialExpiredLocations => {
  /**
   * Current subscription state is used to identify active trials
   * and locations with an active Call Intelligence Pro subscription.
   */
  const {
    trialActiveLocationIds,
    callIntelProPlanLocationIds,
    isLoading: isSubscriptionLoading,
  } = useCallIntelSubscription({ locationIds });

  /**
   * Trial history identifies locations whose latest trial was not
   * followed by a subscription and provides their trial date range.
   */
  const {
    trialExpiredUnsubscribedLocationIds,
    trialExpiredUnsubscribedDateRangeMap,
    isLoading: isTrialHistoryLoading,
  } = useFeatureTrialExpiredLocations({ locationIds, featureEnum: Feature.CALL_INTELLIGENCE });

  // Exclude locations that currently have Trial or Pro access from the expired-trial list.
  const trialExpiredLocationIds = useMemo(
    () =>
      trialExpiredUnsubscribedLocationIds.filter(
        (locationId) =>
          !trialActiveLocationIds.includes(locationId) && !callIntelProPlanLocationIds.includes(locationId)
      ),
    [trialExpiredUnsubscribedLocationIds, trialActiveLocationIds, callIntelProPlanLocationIds]
  );

  /**
   * Keep trial dates only for locations that are actually expired
   * after currently active trials have been excluded.
   */
  const trialExpiredDateRangeMap = useMemo(() => {
    return trialExpiredLocationIds.reduce<Record<string, FeatureTrialDateRange>>((acc, locationId) => {
      const dateRange = trialExpiredUnsubscribedDateRangeMap[locationId];

      if (dateRange) {
        acc[locationId] = dateRange;
      }

      return acc;
    }, {});
  }, [trialExpiredLocationIds, trialExpiredUnsubscribedDateRangeMap]);

  return {
    trialActiveLocationIds,
    trialExpiredLocationIds,
    trialExpiredDateRangeMap,
    callIntelProPlanLocationIds,
    hasActiveTrial: trialActiveLocationIds.length > 0,
    hasExpiredTrial: trialExpiredLocationIds.length > 0,
    hasActiveSubscription: callIntelProPlanLocationIds.length > 0,
    isLoading: isSubscriptionLoading || isTrialHistoryLoading,
  };
};
